import environment from "@/config/environment";
import type { Locale } from "date-fns";
import { enUS, fr } from "date-fns/locale";
import { createInstance, type TFunction } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { useMemo } from "react";
import { initReactI18next, useTranslation as useT } from "react-i18next";
import { useUser, useUserPreference } from "../auth/hooks/use-user";
import { createDateFormat, type DateFormat } from "./dates";
import { useGlobalEffect } from "./use-global-effect";

const languageDefault = "en";
const languageAvailable = ["fr", "en"];

export const languages: {
  [key: string]: string;
} = {
  FR: "Français",
  EN: "English",
};

const i18n = createInstance();

// `index.html` says `lang="fr"` for the first paint; from then on the page
// declares the language it is actually shown in, which is what screen readers,
// hyphenation and the browser's translate offer go by.
i18n.on("languageChanged", (lng) => {
  document.documentElement.lang = lng;
});

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: languageDefault,
    supportedLngs: languageAvailable,
    defaultNS: "app",
    ns: ["app", "public"], // Support multiple namespaces
    backend: {
      // Use a function to route to different directories based on namespace
      loadPath: (lng: string, ns: string) => {
        return `/locales/${ns}/${lng}.json?v=${environment.buildTime}`;
      },
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      bindI18n: "languageChanged",
      bindI18nStore: "",
      transEmptyNodeValue: "",
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ["br", "strong", "i"],
      useSuspense: true,
    },
  });

export const useTranslation = (ns?: string | string[], fileNs = "app") => {
  const { t: i18nT, ...rest } = useT(fileNs);
  const commonValues = {};
  const enhancedT = (
    key: string | string[],
    options: Record<string, unknown> | string = {},
  ) => {
    // `t("some.key", "Texte par défaut")` is i18next's own shorthand for a
    // default value, and it is written all over the views. Spreading that
    // string into the options object turned it into `{0:"T",1:"e",…}` — the
    // default was dropped and the raw key was rendered on screen instead.
    //
    // That default is usually a value off a row (`t(\`results.${r}\`, r)`), and a
    // row hands back `null` for a column nobody filled. `= {}` does not cover it —
    // a parameter default only applies to `undefined` — so `given` was `null` and
    // reading `.replace` off it took the whole list down with it. A null default is
    // no default, which is what the key alone already means.
    const given: Record<string, unknown> =
      typeof options === "string"
        ? { defaultValue: options }
        : options || ({} as Record<string, unknown>);

    const mergedOptions = {
      ...given,
      replace: {
        ...commonValues,
        ...((given.replace as Record<string, unknown>) || given || {}), // User-provided values override common ones
      },
    };
    if (!Array.isArray(key)) {
      key = [key];
    }
    key = [...key.map((k) => `${ns}.${k}`), ...key]; // Try with "translation:" prefix first, then fallback to original key
    return i18nT(key, mergedOptions);
  };
  return { t: enhancedT as TFunction<"translation", undefined>, ...rest };
};

// Helper hook for public chat translations
export const usePublicTranslation = (ns?: string | string[]) => {
  return useTranslation(ns, "public");
};

export const useLanguage = () => {
  const { data: user } = useUser();
  const { currentLanguage, updateLanguage } = useUserPreference();
  let lang = i18n.language?.toLocaleUpperCase() || "EN";
  if (languages[lang as keyof typeof languages] === undefined) {
    lang = "EN";
  }

  useGlobalEffect(
    "use-language",
    () => {
      if (currentLanguage && languages[currentLanguage]) {
        i18n.changeLanguage(currentLanguage.toLocaleLowerCase());
      }
    },
    [currentLanguage],
  );

  const changeLanguage = (lang: string) => {
    lang = lang.toLowerCase();
    i18n.changeLanguage(lang);
    if (user?.id) {
      updateLanguage(lang);
    }
  };

  return { currentLanguage: lang, changeLanguage };
};

// Helper hook for public chat language (stores in localStorage)
export const usePublicLanguage = () => {
  let lang = i18n.language?.toLocaleUpperCase() || "EN";
  if (languages[lang as keyof typeof languages] === undefined) {
    lang = "EN";
  }

  const changeLanguage = (lang: string) => {
    const langLower = lang.toLowerCase();
    i18n.changeLanguage(langLower);
    // Store preference in localStorage for public chat
    try {
      localStorage.setItem("preferredLanguage", langLower);
    } catch {
      // Silent fail if localStorage is not available
    }
  };

  return { currentLanguage: lang, changeLanguage };
};

export const getDateFnsLocale = (language?: string): Locale => {
  const lang = (language || i18n.language || "en").toLowerCase();
  switch (lang) {
    case "fr":
      return fr;
    case "en":
    default:
      return enUS;
  }
};

export const useDateFnsLocale = (): Locale => {
  const { i18n } = useT();
  return getDateFnsLocale(i18n.language);
};

/**
 * Dates in the user's language: `const { format, fromNow, range } =
 * useDateFormat()`. See `dates.ts` for why nothing formats a date without it.
 */
export const useDateFormat = (): DateFormat => {
  const locale = useDateFnsLocale();
  return useMemo(() => createDateFormat(locale), [locale]);
};

export default i18n;
